/**
 * Transforms over the fundamentals series, and the trailing stats above it.
 *
 * Added 2026-09-27 after looking at how VJN presents financials. The idea worth
 * borrowing is not "more charts", it is that a small set of TRANSFORMS over one
 * shared dataset feels like enormous depth while being very little code. Growth
 * is the one that earns its keep: "revenue went up" and "growth is decelerating"
 * are different conclusions from identical data.
 *
 * Everything here is arithmetic on /api/public/history, which already returns
 * quarterly points of { label, price, revenue, fcf, eps }. No new endpoint, no
 * new data, nothing fetched.
 *
 * ⚠ `label` is a fiscal PERIOD END, not a calendar date. Apple's 2021-08-31 is
 * its fiscal Q4. So the quarter filter is labelled FISCAL and derived from each
 * company's own sequence rather than from the calendar month, which would put
 * Apple's Q4 in the calendar Q3 bucket and quietly mislead.
 */

/** Points per year in this series. The API buckets quarterly for long ranges. */
function pointsPerYear(rows) {
  // Two points are enough to measure a cadence, and it matters: after the
  // quarter filter the series is ANNUAL (one point per year), and bailing to a
  // quarterly assumption here made YoY look for a lag of 4 in a 2-point series
  // and return nothing. "Q4 revenue, year over year" is one of the most natural
  // things to ask for and it produced an empty chart.
  if (rows.length < 2) return 4;
  const first = Date.parse(rows[0].label);
  const last = Date.parse(rows[rows.length - 1].label);
  if (!Number.isFinite(first) || !Number.isFinite(last) || last <= first) return 4;
  const years = (last - first) / (365.25 * 24 * 3600 * 1000);
  if (years <= 0) return 4;
  const per = (rows.length - 1) / years;
  // Snap to the plausible cadences rather than trusting a ragged estimate.
  return [1, 2, 4, 12, 52, 252].reduce((a, b) => (Math.abs(b - per) < Math.abs(a - per) ? b : a));
}

/**
 * Re-express a metric as a growth rate. Returns rows with the metric replaced,
 * and the leading points where there is no comparison dropped.
 *
 * @param {'qoq'|'yoy'} mode
 */
export function toGrowth(rows, metric, mode) {
  if (!rows?.length || mode === 'off') return rows;
  const lag = mode === 'yoy' ? pointsPerYear(rows) : 1;
  if (rows.length <= lag) return [];

  const out = [];
  for (let i = lag; i < rows.length; i++) {
    const prev = Number(rows[i - lag]?.[metric]);
    const cur = Number(rows[i]?.[metric]);
    // A growth rate off a negative or zero base is not meaningful — a swing
    // from -10 to 5 is not "150% growth" in any sense worth printing.
    const pct = Number.isFinite(prev) && Number.isFinite(cur) && prev > 0
      ? ((cur - prev) / prev) * 100
      : null;
    out.push({ ...rows[i], [metric]: pct });
  }
  return out;
}

/** Trailing mean over `n` points. n <= 1 is a no-op. */
export function smooth(rows, metric, n) {
  const w = Number(n) || 0;
  if (!rows?.length || w <= 1) return rows;
  return rows.map((r, i) => {
    const slice = rows.slice(Math.max(0, i - w + 1), i + 1)
      .map((x) => Number(x[metric]))
      .filter(Number.isFinite);
    return { ...r, [metric]: slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : null };
  });
}

/**
 * Keep only one fiscal quarter. Position in the company's own 4-point cycle,
 * counted back from the most recent point, so it holds for off-calendar fiscal
 * years. q is 1..4 where 4 is the most recent completed quarter's slot.
 */
export function onlyQuarter(rows, q) {
  if (!rows?.length || !q) return rows;
  const per = pointsPerYear(rows);
  if (per !== 4) return rows;       // not quarterly; filtering would be nonsense
  const out = [];
  for (let i = rows.length - 1; i >= 0; i--) {
    const slot = 4 - ((rows.length - 1 - i) % 4);
    if (slot === q) out.unshift(rows[i]);
  }
  return out;
}

/** Apply the whole transform stack in the order that makes sense. */
export function applyTransforms(rows, metric, { growth = 'off', smoothN = 0, quarter = 0 } = {}) {
  // Quarter first (it thins the series), then growth (needs the original
  // spacing to find its lag), then smoothing last so it averages the thing
  // actually being displayed.
  let out = rows || [];
  if (quarter) out = onlyQuarter(out, quarter);
  if (growth !== 'off') out = toGrowth(out, metric, growth);
  if (smoothN > 1) out = smooth(out, metric, smoothN);
  return out;
}

// ── the numbers above the chart ──────────────────────────────────────────────

function cagr(rows, metric, years) {
  const per = pointsPerYear(rows);
  const lag = Math.round(per * years);
  if (rows.length <= lag) return null;
  const a = Number(rows[rows.length - 1 - lag]?.[metric]);
  const b = Number(rows[rows.length - 1]?.[metric]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0) return null;
  return ((b / a) ** (1 / years) - 1) * 100;
}

/** Trailing twelve months of a flow metric (revenue, fcf, eps). */
function ttm(rows, metric) {
  const per = pointsPerYear(rows);
  if (per !== 4 || rows.length < 4) return null;
  const last4 = rows.slice(-4).map((r) => Number(r[metric])).filter(Number.isFinite);
  return last4.length === 4 ? last4.reduce((a, b) => a + b, 0) : null;
}

/**
 * The header strip: trailing growth and trailing multiples.
 *
 * ⚠ TRAILING ONLY, and labelled as such. VJN's equivalent leans on forward
 * estimates (NTM, next two fiscal years); this app has no analyst-estimate
 * source wired up, and a made-up forward number is worse than none.
 */
export function headerStats(rows) {
  if (!rows?.length) return null;
  const price = Number(rows[rows.length - 1]?.price);

  const growth = {};
  [1, 2, 3, 5].forEach((y) => {
    growth[y] = { revenue: cagr(rows, 'revenue', y), eps: cagr(rows, 'eps', y) };
  });

  const epsTtm = ttm(rows, 'eps');

  // P/E only. P/S would need a share count to turn absolute revenue into a
  // per-share figure, and neither the history nor the grade payload carries
  // one — checked, not assumed. A P/S derived from a guessed share count is
  // worse than no P/S.
  return {
    price: Number.isFinite(price) ? price : null,
    growth,
    pe: Number.isFinite(price) && epsTtm > 0 ? price / epsTtm : null,
    epsTtm,
    revTtm: ttm(rows, 'revenue'),
    fcfTtm: ttm(rows, 'fcf'),
    points: rows.length,
  };
}
